/**
 * 用户操作日志（operation log）—— "删了张照片 / 撤回了一条信 / 改了昵称"
 * 这类可审计操作，POST 到 `/api/v1/operation-logs`，后端落 operation_logs 表，
 * 用于事后追责、风控告警。
 *
 * 设计要点：
 *   - 同步入队：trackOperation() 永远不阻塞业务路径。
 *   - 批 flush：ring buffer 满 20 条或距上次 flush 1s 触发 POST。
 *   - 节流：每分钟最多 60 条（与 clientLog / requestLog 同步约束）。
 *   - silent best-effort：上报失败 → console.warn 一次 → 丢弃本批，**不抛、不重试**。
 *   - PII：context 自动 size 截断 1KB；targetLabel 不强校验，由调用方自律。
 *
 * 用法：
 *   - 业务侧在删除 / 发布 / 撤回等关键路径调一次 `trackOperation(...)`。
 *   - 启动期 `main.tsx` 调一次 `installOperationTracking()`，挂 pagehide 强制 flush。
 *
 * 关闭开关：VITE_OPS_LOG_ENABLED=0 → 所有函数退化为 noop。
 */

import { apiPaths } from '../api/paths';
import { createBatchFlusher, installBatchFlushOnPagehide, type BatchFlusher } from './_logBuffer';

/** 调用方传进的最小契约。 */
export interface TrackInput {
  /** 动作简短归一化名）：'photo.delete' / 'letter.publish' / 'me.avatar.update' */
  action: string;
  /** 资源类型：'photo' / 'letter' / 'meet_story_milestone' / ... */
  resource: string;
  /** 目标资源 id（删 / 改时给；列表浏览可不填） */
  targetId?: string;
  /** 目标资源的人类可读标签（仅用于风控告警 message，禁止记原文 / PII） */
  targetLabel?: string;
  /** 'success' 或 'failure' */
  result: 'success' | 'failure';
  /** 失败时的业务错误码（如 'INVALID_CREDENTIALS' / 'NETWORK'） */
  errorCode?: string;
  /** 与后端 audit 对账的 trace id（来自 ApiCallContext.traceId） */
  traceId?: string;
  /** 与后端 audit 对账的 request id */
  requestId?: string;
  /** 自由上下文；自动按 1KB 截断 JSON.stringify 后的字符串 */
  context?: Record<string, unknown>;
}

/** 实际写入队列 / 上报 body 的 record。 */
export interface OperationLogRecord {
  /** 客户端自生成 id；用于本地去重与日志对账。 */
  client_id: string;
  /** 发生时间（epoch ms）—— 与 BE server/internal/operationlog/types.go:94 的 ClientTS 对齐 */
  client_ts: number;
  action: string;
  resource: string;
  target_id?: string;
  target_label?: string;
  result: 'success' | 'failure';
  error_code?: string;
  trace_id?: string;
  request_id?: string;
  context?: Record<string, unknown>;
}

const CONTEXT_SIZE_LIMIT = 1024;
const WARN_SAMPLE_INTERVAL_MS = 60_000;

function readFlag(): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = (import.meta as any)?.env;
    if (!env) return true;
    const v = env.VITE_OPS_LOG_ENABLED;
    if (v === '0' || v === 0 || v === false) return false;
    return true;
  } catch {
    return true;
  }
}

let enabled = readFlag();

/** 测试 / 调试：手动切换开关。生产构建由 readFlag 锁死。 */
export function _setOperationLogEnabled(v: boolean): void {
  enabled = v;
}

/** 字符串大小估算：JSON.stringify 后按字节长度计算（粗略按 UTF-16）。 */
function approxSize(value: unknown): number {
  if (value === undefined || value === null) return 0;
  try {
    return JSON.stringify(value).length;
  } catch {
    return 0;
  }
}

/** 截断 context：JSON 化后超 1KB → 只保留 keys 摘要（value 全部丢弃）。 */
function truncateContext(ctx: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!ctx) return undefined;
  if (approxSize(ctx) <= CONTEXT_SIZE_LIMIT) return ctx;
  const keys = Object.keys(ctx);
  return { _truncated: true, _keys: keys };
}

/** 生成 client_id：crypto.randomUUID()（带退化路径）。 */
function nextClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  let out = '';
  for (let i = 0; i < 32; i++) {
    out += Math.floor(Math.random() * 16).toString(16);
  }
  return out;
}

let flusher: BatchFlusher<OperationLogRecord> | null = null;
let lastWarnAt = 0;

function warnOnce(msg: string): void {
  const now = Date.now();
  if (now - lastWarnAt < WARN_SAMPLE_INTERVAL_MS) return;
  lastWarnAt = now;
  try {
    // eslint-disable-next-line no-console
    console.warn(`[operationLog] ${msg}`);
  } catch {
    /* swallow */
  }
}

function getFlusher(): BatchFlusher<OperationLogRecord> | null {
  if (!enabled) return null;
  if (flusher) return flusher;
  flusher = createBatchFlusher<OperationLogRecord>({
    url: apiPaths.operationLogs.ingest,
    name: 'operationLog',
    encodeBody: (items) => JSON.stringify({ items: [...items] }),
  });
  return flusher;
}

/**
 * 同步上报一条用户操作。永远不抛、不重试。
 * VITE_OPS_LOG_ENABLED=0 时为 noop。
 */
export function trackOperation(input: TrackInput): void {
  if (typeof window === 'undefined') return;
  if (!enabled) return;
  try {
    const f = getFlusher();
    if (!f) return;
    const rec: OperationLogRecord = {
      client_id: nextClientId(),
      client_ts: Date.now(),
      action: input.action,
      resource: input.resource,
      target_id: input.targetId,
      target_label: input.targetLabel,
      result: input.result,
      error_code: input.errorCode,
      trace_id: input.traceId,
      request_id: input.requestId,
      context: truncateContext(input.context),
    };
    f.enqueue(rec);
  } catch (e) {
    warnOnce(`trackOperation failed: ${e instanceof Error ? e.message : String(e)}`);
  }
}

/**
 * 启动期调用：把 pagehide / visibilitychange 装好；幂等。
 * VITE_OPS_LOG_ENABLED=0 时退化为 noop。
 */
let installed = false;
export function installOperationTracking(): () => void {
  if (installed || typeof window === 'undefined') {
    return () => undefined;
  }
  if (!enabled) {
    installed = true;
    return () => undefined;
  }
  installed = true;
  const f = getFlusher();
  if (!f) return () => undefined;
  return installBatchFlushOnPagehide([f as BatchFlusher<unknown>]);
}

/** 测试辅助：返回当前队列深度。 */
export function _peekOperationLogQueueSize(): number {
  return flusher?.size() ?? 0;
}

/** 测试辅助：强制 flush。 */
export function _flushOperationLogs(): Promise<void> {
  return flusher?.flushNow() ?? Promise.resolve();
}